import * as React from 'react';
import { HealthProfilePageWrapper } from '@leagueplatform/health-profile-common';
import {
  css,
  Flex,
  genesisStyled,
  HeadingOne,
  Text,
} from '@leagueplatform/genesis-commons';
import { useIntl } from '@leagueplatform/locales';
import { MetricType } from '@leagueplatform/dashboard-api';
import { useHealthSources } from '#hooks/use-get-health-sources.hook.ts';
import {
  LoadingIndicator,
  SharedErrorStateProps,
} from '@leagueplatform/web-common-components';
import {
  usePageViewAnalytics,
  SUB_PRODUCT_AREA,
} from '@leagueplatform/analytics';
import { PRODUCT_AREA, SCREEN_NAMES } from '#constants/analytics.ts';
import { ErrorPanel } from '#components/error-panel/index.ts';
import { useHealthMetricDocumentTitle } from '#hooks/use-health-metric-document-title.ts';
import { useInfiniteHealthMetricsV3 } from '#hooks/use-get-health-metrics-v3.hook.ts';
import { useParamsLegacy as useParams } from '@leagueplatform/routing';
import { DataSourceItem } from './data-source.item';

type Params = {
  dataType: MetricType;
};

interface DataSourcesItemsProps extends SharedErrorStateProps {
  isRefetching: boolean;
  refetch: () => void;
  isError: boolean;
  children: React.ReactElement;
}

const DataSourcesWidth = '486px';
const DataTypeText = genesisStyled('span')(
  css({
    fontWeight: 'bold',
  }),
);

function DataSourcesItems({
  children,
  isError,
  refetch,
  isRefetching,
  error,
}: DataSourcesItemsProps) {
  if (isError) {
    return (
      <ErrorPanel error={error} isRefetching={isRefetching} onRetry={refetch} />
    );
  }
  return children;
}

function DataSourcesPageWrapper({ children }: React.PropsWithChildren<{}>) {
  const { formatMessage } = useIntl();
  return (
    <HealthProfilePageWrapper
      title={formatMessage({
        id: 'DATA_SOURCES',
      })}
    >
      {children}
    </HealthProfilePageWrapper>
  );
}

export function DataSourcesV3() {
  const { dataType } = useParams<Params>();
  const { formatMessage } = useIntl();

  usePageViewAnalytics({
    product_area: PRODUCT_AREA.DASHBOARD,
    screen_name: SCREEN_NAMES.VIEW_DATA_SOURCES,
    metric_type: dataType,
    sub_product_area: SUB_PRODUCT_AREA.DASHBOARD,
  });
  const {
    isLoading: isLoadingHealthSources,
    isRefetching: isRefetchingHealthSources,
    isError: isErrorHealthSources,
    data: healthSources,
    refetch: refetchHealthSources,
    error: healthSourcesError,
  } = useHealthSources({
    filter: { type: dataType },
  });
  const {
    isLoading: isLoadingHealthMetrics,
    isRefetching: isRefetchingHealthMetrics,
    isError: isErrorHealthMetrics,
    data: healthMetrics,
    refetch: refetchHealthMetics,
    error: healthMetricsError,
  } = useInfiniteHealthMetricsV3({
    filter: {
      type: dataType,
    },
    fields: { type: ['type', 'value', 'source', 'unit', 'timestamp'] },
  });
  const isRefetching = isRefetchingHealthSources || isRefetchingHealthMetrics;
  const isHealthPlanIncluded =
    healthMetrics?.pages[0].data[0].metricComponents?.data.some(
      (metricComponent) => metricComponent.sourceType === 'health_plan',
    );
  const isSelfReportedIncluded =
    healthMetrics?.pages[0].data[0].metricComponents?.data.some(
      (metricComponent) =>
        metricComponent.sourceType === 'self_reported' ||
        metricComponent.sourceType === 'self_reported_no_input',
    );

  const noSourceData = !isHealthPlanIncluded && !healthSources?.data.length;

  const isOnlySelfReported = isSelfReportedIncluded && noSourceData;

  const metricDisplayName = healthMetrics?.pages[0].data[0].metricDisplayName;
  useHealthMetricDocumentTitle(
    formatMessage({
      id: 'DATA_SOURCES',
    }),
    metricDisplayName,
  );

  if (isLoadingHealthSources || isLoadingHealthMetrics) {
    return <LoadingIndicator />;
  }

  // if healthSources has an error && health plan is not included && self reported is not included, we display the error panel
  const isError =
    (isErrorHealthSources &&
      !isHealthPlanIncluded &&
      !isSelfReportedIncluded) ||
    isErrorHealthMetrics;

  if (isError) {
    return (
      <DataSourcesPageWrapper>
        <ErrorPanel
          isRefetching={isRefetching}
          onRetry={() => {
            refetchHealthSources();
            refetchHealthMetics();
          }}
          error={[healthSourcesError, healthMetricsError]}
        />
      </DataSourcesPageWrapper>
    );
  }

  return (
    <DataSourcesPageWrapper>
      <Flex flexDirection="column" maxWidth={DataSourcesWidth}>
        <HeadingOne as="h2" paddingBottom="oneAndHalf" paddingTop="oneAndHalf">
          {metricDisplayName}
        </HeadingOne>
        <Text paddingBottom="oneAndHalf">
          {formatMessage({
            id: 'SOURCES_CAN_UPDATE',
          })}{' '}
          <DataTypeText>{metricDisplayName}</DataTypeText>.
        </Text>
        <Text paddingBottom="oneAndHalf">
          {formatMessage({
            id: 'WHEN_MULTIPLE_SOURCES_AVAILABLE',
          })}
        </Text>
        <DataSourcesItems
          isError={isError}
          refetch={() => {
            refetchHealthSources();
            refetchHealthMetics();
          }}
          isRefetching={isRefetching}
          error={[healthSourcesError, healthMetricsError]}
        >
          {isOnlySelfReported ? (
            <DataSourceItem
              dataSource={{
                type: dataType,
                device: 'no_source_data',
              }}
            />
          ) : (
            <>
              {healthSources?.data.map((healthSource) => (
                <DataSourceItem
                  key={healthSource.id}
                  dataSource={healthSource}
                />
              ))}
              {isHealthPlanIncluded && (
                <DataSourceItem
                  dataSource={{
                    type: dataType,
                    device: 'health_plan',
                  }}
                />
              )}
            </>
          )}
        </DataSourcesItems>
      </Flex>
    </DataSourcesPageWrapper>
  );
}
